import {
  assessmentScaleOf,
  type AssessmentScale,
  type WidaStudent,
  type WidaYearRecord,
} from '../Upload/widaHistory';
import type { SisStudent } from '../Upload/sisRoster';
import { classifyElStatus, loadThresholds, type Thresholds } from '../../core/thresholds';
import type { StudentTenure } from '../../core/classificationTenure';
import {
  getReclassificationCriteria,
  type ReclassCriteria,
  type ReclassDomainMinimum,
} from '../Reclassification/reclassCriteria';
import { currentCapabilityPhrase, nextCapabilityPhrase } from './planContent';
import { shortLabelForEndYear } from '../../core/schoolYear';

export type PlanStudent = WidaStudent & {
  sis?: SisStudent | null;
  tenure?: StudentTenure;
  thresholds?: Thresholds;
};

export type DomainKey = 'listening' | 'speaking' | 'reading' | 'writing';
export type SeriesKey = DomainKey | 'composite';

export const DOMAINS: DomainKey[] = ['listening', 'speaking', 'reading', 'writing'];

export const DOMAIN_LABELS: Record<SeriesKey, string> = {
  listening: 'Listening',
  speaking: 'Speaking',
  reading: 'Reading',
  writing: 'Writing',
  composite: 'Composite',
};

export type PlanCriteria = ReclassCriteria & { domainMinimums?: ReclassDomainMinimum[]; outcome?: 'exits' | 'eligible_for_review'; displayNote?: string; conditions?: { measure: string; minimum: number }[]; review?: { conditions: { measure: string; minimum: number }[]; displayNote?: string } };

export const DEFAULT_RECLASS_CRITERIA: ReclassCriteria = getReclassificationCriteria('Utah')!;

export function planDomainMinimums(criteria: PlanCriteria): ReclassDomainMinimum[] {
  if (criteria.domainMinimums) return criteria.domainMinimums;
  return criteria.domainMinimum ? [criteria.domainMinimum] : [];
}

function domainKeysOf(domain: string): DomainKey[] {
  return domain
    .toLowerCase()
    .split(/\s+and\s+|\s*,\s*/)
    .map((token) => token.trim())
    .filter((token): token is DomainKey => (DOMAINS as string[]).includes(token));
}

export function domainMinimumFor(criteria: PlanCriteria, domain: DomainKey): number | null {
  const dm = planDomainMinimums(criteria).find((m) => domainKeysOf(m.domain).includes(domain));
  return dm ? dm.value : null;
}

export function speakingMinimum(criteria: PlanCriteria): number | null {
  return domainMinimumFor(criteria, 'speaking');
}

const LEVEL_LABELS = ['', 'Entering', 'Emerging', 'Developing', 'Expanding', 'Bridging', 'Reaching'];

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function fmt(level: number): string {
  return level.toFixed(1);
}

function signed(delta: number): string {
  return delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1);
}

export function levelLabel(level: number | null): string {
  if (level == null) return '—';
  const idx = Math.min(6, Math.max(1, Math.floor(level)));
  return LEVEL_LABELS[idx] ?? '—';
}

export const ALTERNATE_SCALE = {
  levelMin: 1,
  levelMax: 5,
  scaleMin: 900,
  scaleMax: 980,
  ladderSegments: 5,
} as const;

const ALT_LEVEL_LABELS = ['', 'Entering', 'Emerging', 'Developing', 'Expanding', 'Bridging'];

function altLevelIndex(level: number): number {
  return Math.min(ALTERNATE_SCALE.levelMax, Math.max(ALTERNATE_SCALE.levelMin, Math.floor(level)));
}

export function altLevelLabel(level: number | null): string {
  if (level == null) return '—';
  return ALT_LEVEL_LABELS[altLevelIndex(level)] ?? '—';
}

export function assessmentScale(student: PlanStudent): AssessmentScale {
  const scored = student.years.filter((y) => !y.waived);
  const last = scored[scored.length - 1];
  if (last) return assessmentScaleOf(last);
  const tagged = [...student.years].reverse().find((y) => y.assessmentScale != null);
  if (tagged) return assessmentScaleOf(tagged);
  return student.sis?.alternateAccess ? 'alternate_access' : 'access';
}

export function accessYearsOnly<T extends WidaStudent>(student: T): T {
  return {
    ...student,
    years: student.years.filter((y) => assessmentScaleOf(y) === 'access'),
  };
}

export function altAccess(student: PlanStudent): boolean {
  return assessmentScale(student) === 'alternate_access';
}

export function altExitPending(student: PlanStudent): boolean {
  return altAccess(student);
}

export function scaleLabel(
  student: PlanStudent,
  level: number | null,
  scale: number | null,
): string {
  if (!altAccess(student)) {
    return level == null ? '—' : `${levelLabel(level)} (${fmt(level)})`;
  }
  if (level == null) return 'Alternate ACCESS · —';
  const base = `Alternate ACCESS · Level ${altLevelIndex(level)} — ${altLevelLabel(level)}`;
  return scale != null ? `${base} · ${scale}` : base;
}

export type SeriesPoint = { year: number; level: number; scale: number | null };

export function domainSeries(student: WidaStudent, key: SeriesKey): SeriesPoint[] {
  return student.years
    .filter((y) => !y.waived && y[key].level != null)
    .map((y: WidaYearRecord) => ({ year: y.year, level: y[key].level as number, scale: y[key].scale }));
}

export const WIDA_SCALE_RESET_YEAR = 2026;

export function detectResetYear(student: WidaStudent): number | null {
  const scored = student.years.filter((y) => !y.waived && y.composite.level != null);
  const hasPre = scored.some((y) => y.year < WIDA_SCALE_RESET_YEAR);
  const hasPost = scored.some((y) => y.year >= WIDA_SCALE_RESET_YEAR);
  return hasPre && hasPost ? WIDA_SCALE_RESET_YEAR : null;
}

export function formatSchoolYear(year: number): string {
  return shortLabelForEndYear(year);
}

export function splitSeriesByReset<T extends { year: number }>(
  points: T[],
  resetYear: number | null,
): { pre: T[]; post: T[] } {
  if (resetYear == null) return { pre: points, post: [] };
  return {
    pre: points.filter((p) => p.year < resetYear),
    post: points.filter((p) => p.year >= resetYear),
  };
}

export function currentLevel(student: WidaStudent, key: SeriesKey): number | null {
  const series = domainSeries(student, key);
  const last = series[series.length - 1];
  return last ? last.level : null;
}

export function priorLevel(student: WidaStudent, key: SeriesKey): number | null {
  const series = domainSeries(student, key);
  const prev = series[series.length - 2];
  return prev ? prev.level : null;
}

export type Trend = {
  delta: number | null;
  category: 'rising' | 'flat' | 'falling' | 'na';
  label: string;
};

export function domainTrend(student: WidaStudent, key: SeriesKey): Trend {
  const series = domainSeries(student, key);
  const a = series[series.length - 2];
  const b = series[series.length - 1];
  if (!a || !b) return { delta: null, category: 'na', label: 'No prior data' };
  const delta = round1(b.level - a.level);
  if (delta >= 0.2) return { delta, category: 'rising', label: `+${delta.toFixed(1)}/year` };
  if (delta <= -0.05) return { delta, category: 'falling', label: `${delta.toFixed(1)}/year` };
  return { delta, category: 'flat', label: 'Flat' };
}

export function accessLevel(level: number | null): number | null {
  return level != null && level >= 1 && level <= 6 ? round1(level) : null;
}

const ALL_SERIES: SeriesKey[] = [...DOMAINS, 'composite'];

function currentEra<T extends { year: number }>(points: T[]): T[] {
  const { pre, post } = splitSeriesByReset(points, WIDA_SCALE_RESET_YEAR);
  return pre.length > 0 && post.length > 0 ? post : points;
}

export function levelChange(student: WidaStudent, key: SeriesKey): number | null {
  const series = currentEra(domainSeries(student, key));
  const a = series[series.length - 2];
  const b = series[series.length - 1];
  if (!a || !b || b.year - a.year !== 1) return null;
  const prior = accessLevel(a.level);
  const current = accessLevel(b.level);
  if (prior == null || current == null) return null;
  return round1(current - prior);
}

export function scoredAdministrations(student: WidaStudent, key: SeriesKey | 'any'): number {
  const scored =
    key === 'any'
      ? currentEra(student.years.filter((y) => !y.waived && ALL_SERIES.some((k) => y[k].level != null)))
      : currentEra(domainSeries(student, key));
  return Math.min(scored.length, 30);
}

export function latestAdministration(student: WidaStudent): WidaYearRecord | null {
  const scored = student.years.filter((y) => !y.waived && ALL_SERIES.some((k) => y[k].level != null));
  return scored[scored.length - 1] ?? null;
}

export function latestAdministrationLevel(student: WidaStudent, key: SeriesKey): number | null {
  return accessLevel(latestAdministration(student)?.[key].level ?? null);
}

export function domainScores(student: WidaStudent, except?: DomainKey): Record<string, number> {
  const scores: Record<string, number> = {};
  const latest = latestAdministration(student);
  if (!latest) return scores;
  for (const domain of DOMAINS) {
    if (domain === except) continue;
    const level = accessLevel(latest[domain].level);
    if (level != null) scores[DOMAIN_LABELS[domain]] = level;
  }
  return scores;
}

export function yearsInEl(student: WidaStudent): number | null {
  return student.yearsInEl;
}

export function isLtel(student: PlanStudent, ltelMinYears = (student.thresholds ?? loadThresholds()).ltelMinYears): boolean {
  if (student.tenure) return student.tenure.ltel.isLtel;
  return student.yearsInEl != null && student.yearsInEl >= ltelMinYears;
}

export function ltelBannerYears(student: PlanStudent): number | null {
  if (!student.tenure) return student.yearsInEl;
  const { yearsInProgram, observedYearsFloor } = student.tenure.tenure;
  const years = Math.max(yearsInProgram ?? 0, observedYearsFloor ?? 0);
  return years > 0 ? years : null;
}

export function isNewcomer(student: PlanStudent): boolean {
  if (student.tenure) return student.tenure.newcomer.isNewcomer;
  return classifyElStatus(student.yearsInEl, student.thresholds ?? loadThresholds()) === 'newcomer';
}

export function belowExit(student: PlanStudent, criteria: PlanCriteria, key: SeriesKey): boolean {
  if (criteria.outcome === 'eligible_for_review') return false;
  const level = currentLevel(student, key);
  const bar = key === 'composite' ? criteria.compositeMin : domainMinimumFor(criteria, key);
  return level != null && bar != null && level < bar && !isNewcomer(student);
}

export function elFlagSuffix(
  yearsInEl: number | null | undefined,
  thresholds: Thresholds = loadThresholds(),
): string {
  const status = classifyElStatus(yearsInEl, thresholds);
  if (status === 'ltel') return ' \u00b7 LTEL';
  if (status === 'newcomer') return ' \u00b7 Newcomer';
  return '';
}

export type ReclassRow = {
  label: string;
  current: number | null;
  required: number;
  met: boolean;
  gap: number | null;
};

export function reclassificationRows(student: WidaStudent, criteria: PlanCriteria): ReclassRow[] {
  if (altAccess(student)) return [];
  const rows: ReclassRow[] = [];

  if (criteria.compositeMin != null) {
    const composite = currentLevel(student, 'composite');
    const required = criteria.compositeMin;
    rows.push({
      label: 'Composite',
      current: composite,
      required,
      met: composite != null && composite >= required,
      gap: composite != null ? round1(required - composite) : null,
    });
  }

  for (const domainMin of planDomainMinimums(criteria)) {
    for (const key of domainKeysOf(domainMin.domain)) {
      const level = currentLevel(student, key);
      rows.push({
        label: `${DOMAIN_LABELS[key]} · domain minimum`,
        current: level,
        required: domainMin.value,
        met: level != null && level >= domainMin.value,
        gap: level != null ? round1(domainMin.value - level) : null,
      });
    }
  }

  return rows;
}

export function domainEvidence(student: WidaStudent, key: SeriesKey): string {
  const label = DOMAIN_LABELS[key];
  const series = domainSeries(student, key);
  const last = series[series.length - 1];
  if (!last) return `No scored ${label} administrations on file.`;
  const prev = series[series.length - 2];
  if (!prev) return `${label} is at ${fmt(last.level)} (${levelLabel(last.level)}).`;
  const delta = round1(last.level - prev.level);
  return `${label} moved ${fmt(prev.level)} → ${fmt(last.level)} this year (${signed(delta)}).`;
}

export function domainWhatsHappening(student: WidaStudent, key: SeriesKey): string {
  const label = DOMAIN_LABELS[key];
  const series = domainSeries(student, key);
  const first = series[0];
  const last = series[series.length - 1];
  if (series.length < 2 || !first || !last) return domainEvidence(student, key);
  const total = round1(last.level - first.level);
  const trend = domainTrend(student, key);
  const trendText =
    trend.category === 'rising'
      ? `still climbing (${trend.label})`
      : trend.category === 'falling'
        ? `slipping (${trend.label})`
        : 'flat year-over-year';
  const movement =
    total > 0
      ? `rose from ${fmt(first.level)} to ${fmt(last.level)} (${signed(total)})`
      : total < 0
        ? `fell from ${fmt(first.level)} to ${fmt(last.level)} (${signed(total)})`
        : `held steady at ${fmt(last.level)}`;
  return `Over ${series.length} scored administrations, ${label} ${movement}; the latest year is ${trendText}.`;
}

const PENDING_CAPABILITY = 'Insufficient assessment data to summarize this domain yet.';

export function hasWidaDescriptorLibrary(criteria: ReclassCriteria): boolean {
  return /wida/i.test(criteria.assessment);
}

function nextLevelLabel(level: number): string {
  return levelLabel(Math.min(6, Math.floor(level) + 1));
}

export function capabilities(student: WidaStudent, criteria: ReclassCriteria): string[] {
  const wida = hasWidaDescriptorLibrary(criteria);
  return DOMAINS.map((domain) => {
    const level = currentLevel(student, domain);
    if (level == null) return `${DOMAIN_LABELS[domain]}: ${PENDING_CAPABILITY}`;
    if (!wida) {
      return `${DOMAIN_LABELS[domain]}: ${levelLabel(level)} on ${criteria.assessment} (${fmt(level)}).`;
    }
    const phrase = currentCapabilityPhrase(domain, level) ?? '';
    return `${DOMAIN_LABELS[domain]}: ${phrase} (${levelLabel(level)}, ${fmt(level)}).`;
  });
}

type DevEdge = {
  domain: DomainKey;
  level: number;
  next: string;
  belowMinGap: number | null;
  min: number | null;
};

function renderDevEdge(edge: DevEdge, criteria: ReclassCriteria, wida: boolean): string {
  if (!wida) {
    return `${DOMAIN_LABELS[edge.domain]}: Working toward the ${nextLevelLabel(edge.level)} performance level on ${criteria.assessment} (currently ${levelLabel(edge.level)}, ${fmt(edge.level)}).`;
  }
  const base = `Working toward ${edge.next}, currently ${levelLabel(edge.level)} (${fmt(edge.level)}) in ${DOMAIN_LABELS[edge.domain]}`;
  const suffix =
    edge.belowMinGap != null && edge.min != null
      ? `; ${edge.belowMinGap.toFixed(1)} below the ${edge.min} state minimum`
      : '';
  return `${base}${suffix}.`;
}

export function areasForDevelopment(student: WidaStudent, criteria: ReclassCriteria): string[] {
  const wida = hasWidaDescriptorLibrary(criteria);
  const edges: DevEdge[] = [];
  for (const domain of DOMAINS) {
    const level = currentLevel(student, domain);
    if (level == null) continue;
    const next = nextCapabilityPhrase(domain, level);
    if (next == null) continue;
    const min = domainMinimumFor(criteria, domain);
    const belowMinGap = min != null && level < min ? round1(min - level) : null;
    const rising = domainTrend(student, domain).category === 'rising';
    if (belowMinGap == null && (rising || level >= 4)) continue;
    edges.push({ domain, level, next, belowMinGap, min });
  }
  edges.sort((a, b) => {
    if (a.belowMinGap != null && b.belowMinGap != null) return b.belowMinGap - a.belowMinGap;
    if (a.belowMinGap != null) return -1;
    if (b.belowMinGap != null) return 1;
    return a.level - b.level;
  });
  return edges.slice(0, 4).map((edge) => renderDevEdge(edge, criteria, wida));
}

export function proficiencySummary(student: WidaStudent, criteria: ReclassCriteria): string {
  const first = student.firstName || 'This student';
  const composite = currentLevel(student, 'composite');
  const speaking = currentLevel(student, 'speaking');
  const speakingMin = speakingMinimum(criteria);
  const compositeMin = criteria.compositeMin;
  const parts: string[] = [];
  if (composite != null) {
    parts.push(`${first} is at composite ${fmt(composite)} (${levelLabel(composite)}).`);
  }
  if (compositeMin != null && composite != null) {
    parts.push(
      composite >= compositeMin
        ? `That meets ${criteria.state}'s ${compositeMin} composite threshold.`
        : `That is ${round1(compositeMin - composite).toFixed(1)} below ${criteria.state}'s ${compositeMin} composite threshold.`,
    );
  }
  if (speaking != null && speakingMin != null) {
    parts.push(
      speaking >= speakingMin
        ? `Speaking (${fmt(speaking)}) clears the ${speakingMin} minimum.`
        : `Speaking (${fmt(speaking)}) is below the ${speakingMin} minimum.`,
    );
  }
  const ltelYears = ltelBannerYears(student);
  if (isLtel(student) && ltelYears != null) {
    parts.push(`Currently classified as LTEL in Year ${ltelYears}.`);
  }
  return parts.join(' ');
}
